import React from "react";
import clsx from "clsx";
import { Button as BootstrapButton } from "react-bootstrap";

import { Skeleton } from "components";

type ButtonVariant =
	| "primary"
	| "secondary"
	| "success"
	| "danger"
	| "warning"
	| "link"
	| "pure-link"
	| "outline-primary"
	| "secondary-danger"
	| "secondary-warning"
	| "secondary-success";

type ButtonProps = {
	onClick?: React.MouseEventHandler<HTMLElement> | any;
	value?: string;
	title?: string;
	variant?: ButtonVariant;
	shape?: "circle";
	size?: "sm" | "lg";
	type?: "button" | "reset" | "submit";
	btnIcon?: boolean;
	icon?: React.ReactNode;
	iconPosition?: "right" | "left";
	minWidth?: string;
	skeletonWidth?: string;
	active?: boolean;
	disabled?: boolean;
	block?: boolean;
	isLoading?: boolean;
	href?: string;
	target?: string;
	as?: React.ElementType;
	tabIndex?: number;
	className?: string | null;
	style?: Object; // ex. { width: "150px" }
	children?: React.ReactNode;
	to?: string;
	form?: string;
	ref?: React.MutableRefObject<HTMLButtonElement>;
};

const Button = ({
	onClick,
	title,
	value,
	variant = "primary",
	shape,
	size,
	type = "button",
	btnIcon = false,
	icon,
	iconPosition = "left",
	minWidth,
	active = false,
	disabled = false,
	block = false,
	isLoading = false,
	skeletonWidth,
	href,
	target,
	as,
	tabIndex,
	className,
	style,
	children,
	to,
	form,
	ref,
}: ButtonProps) => {
	const handleOnClick = e => {
		// var used for prevent double click
		const { detail }: { detail: number } = e;

		if (disabled) {
			return;
		}

		if (typeof onClick === "function" && detail === 1) {
			onClick(e);
		}
	};

	const shapeClass = shape === "circle" && "ar-btn-circle";
	const classBtnIcon = btnIcon && "ar-btn-icon";
	const skeletonVariant = shape === "circle" ? "circle" : "rect";
	const disabledClass = disabled ? "ar-btn-disabled" : "";

	return (
		<BootstrapButton
			className={clsx(shapeClass, className, classBtnIcon, disabledClass)}
			onClick={handleOnClick}
			disabled={disabled || isLoading}
			variant={variant}
			bsPrefix="ar-btn"
			active={active}
			as={as}
			block={block}
			href={href}
			size={size}
			target={target}
			type={type}
			tabIndex={tabIndex}
			style={{
				...style,
				minWidth,
			}}
			title={title}
			form={form}
			to={to}
			ref={ref}
		>
			{isLoading && (
				<Skeleton
					variant={skeletonVariant}
					width={skeletonWidth}
					className="skeletonInBtn"
				/>
			)}
			<>
				{icon && iconPosition === "left" && (
					<span className="icon">{icon}</span>
				)}
				{(value || children) && <span>{children || value}</span>}
				{icon && iconPosition === "right" && (
					<span className="icon">{icon}</span>
				)}
			</>
		</BootstrapButton>
	);
};

export default Button;
